import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { isAdminFromToken } from "../../../utils/authToken";
import { fmtDateTime } from "../campanhasApi";
import { getCreativesStatusApi, listCreativesApi, setReferenceApi, type CreativeListItem, type CreativesStatus } from "../criativosApi";
import { CreativeImage, ReviewPill } from "../components/CreativeParts";
import { CAMPANHAS_PAGE, CRIATIVO_NOVO_PAGE, CRIATIVO_PAGE } from "../routes";
import "./Campanhas.css";
import "./Criativos.css";

/** Biblioteca de criativos feitos com a Eco-IA e o criativo padrão que serve de modelo. */
const CriativosPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(CAMPANHAS_PAGE());
  const [params, setParams] = useSearchParams();
  const campaignFilter = params.get("campanha") ?? "";
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = useMemo(() => isAdminFromToken(token), [token]);
  const [status, setStatus] = useState<CreativesStatus | null>(null);
  const [items, setItems] = useState<CreativeListItem[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadStatus = () => getCreativesStatusApi().then(setStatus).catch((e) => toast.error(e?.message));

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    setItems(null);
    listCreativesApi(campaignFilter || undefined)
      .then(setItems)
      .catch((e) => {
        toast.error(e?.message);
        setItems([]);
      });
  }, [campaignFilter]);

  const campaigns = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of items ?? []) if (i.campaign) map.set(i.campaign.id, i.campaign.name);
    return Array.from(map.entries());
  }, [items]);

  const uploadReference = async (file: File | undefined) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) return toast.error("O criativo padrão precisa ser JPG ou PNG.");
    try {
      setUploading(true);
      await setReferenceApi(file);
      toast.success("Criativo padrão trocado. Os próximos criativos seguem este modelo.");
      await loadStatus();
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const disabledReason = !status
    ? null
    : !status.imageEnabled
      ? "O gerador de imagens não está configurado no servidor."
      : !status.reference
        ? isAdmin
          ? "Cadastre o criativo padrão abaixo para começar."
          : "Um administrador precisa cadastrar o criativo padrão."
        : null;

  return (
    <div className="cmpPage">
      <main className="cmpPage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate("/") },
            { label: "Campanhas", onClick: goBack },
            { label: "Criativos" },
          ]}
          title="Criativos"
          subtitle="Artes dos anúncios feitas pela Eco-IA a partir do criativo padrão da empresa. Cada ajuste vira uma versão nova; nada se perde."
          actions={
            <button
              type="button"
              className="ecoBtn ecoBtn--primary"
              disabled={!status || Boolean(disabledReason)}
              title={disabledReason ?? undefined}
              onClick={() => navigate(CRIATIVO_NOVO_PAGE(campaignFilter || undefined))}
            >
              <EcoIcon name="plus" size={18} />
              Novo criativo
            </button>
          }
        />

        {disabledReason ? (
          <p className="cmpNotice cmpNotice--info">
            <EcoIcon name="alertTriangle" size={16} />
            <span>{disabledReason}</span>
          </p>
        ) : null}

        <section className="ecoCard cmpPanel crvRef">
          <div className="crvRef__image">
            {status?.reference ? (
              <CreativeImage id="reference" refreshKey={status.reference.id} className="crvRef__img" alt="Criativo padrão da empresa" />
            ) : (
              <div className="crvRef__img crvImg--empty" />
            )}
          </div>
          <div className="crvRef__body">
            <h2 className="cmpPanel__title">Criativo padrão</h2>
            <p className="cmpHint">
              É o modelo de todas as artes: cores, logotipo, fontes, blocos e rodapé. A Eco-IA leu o estilo dele e o segue em cada criativo novo.
            </p>
            {status?.reference ? (
              <p className="cmpSub">
                {status.reference.fileName} · desde {fmtDateTime(status.reference.createdAt)}
              </p>
            ) : (
              <p className="cmpSub">Nenhum cadastrado.</p>
            )}
            <div className="cmpActions crvRef__actions">
              {status?.reference?.styleGuide ? (
                <button type="button" className="ecoBtn" onClick={() => setShowGuide((v) => !v)} aria-expanded={showGuide}>
                  <EcoIcon name="eye" size={16} />
                  {showGuide ? "Esconder o estilo lido" : "Ver o estilo lido pela Eco-IA"}
                </button>
              ) : null}
              {isAdmin ? (
                <label className={`ecoBtn${uploading ? " is-disabled" : ""}`}>
                  <EcoIcon name="camera" size={16} />
                  {uploading ? "Lendo o estilo..." : status?.reference ? "Trocar criativo padrão" : "Cadastrar criativo padrão"}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    className="ecoVisuallyHidden"
                    disabled={uploading}
                    onChange={(e) => uploadReference(e.target.files?.[0])}
                  />
                </label>
              ) : null}
            </div>
            {showGuide && status?.reference?.styleGuide ? <pre className="crvGuide">{status.reference.styleGuide}</pre> : null}
          </div>
        </section>

        <section className="ecoCard cmpPanel cmpPanel--flush">
          <div className="cmpPanel__head">
            <h2 className="cmpPanel__title">Biblioteca</h2>
            {campaigns.length || campaignFilter ? (
              <label className="crvFilter">
                <span className="ecoLabel">Campanha</span>
                <select
                  className="ecoInput"
                  value={campaignFilter}
                  onChange={(e) => setParams(e.target.value ? { campanha: e.target.value } : {}, { replace: true })}
                >
                  <option value="">Todas</option>
                  {campaigns.map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
          {!items ? (
            <p className="cmpLoading">Carregando...</p>
          ) : !items.length ? (
            <div className="cmpPanel__pad">
              <p className="ecoEmpty">Nenhum criativo ainda. Clique em "Novo criativo": a Eco-IA escreve os textos e desenha a arte.</p>
            </div>
          ) : (
            <ul className="crvGrid">
              {items.map((c) => (
                <li key={c.rootId}>
                  <button type="button" className="crvCard" onClick={() => navigate(CRIATIVO_PAGE(c.id))}>
                    <CreativeImage id={c.id} className={`crvCard__img${c.format === "STORY" ? " crvCard__img--story" : ""}`} alt={`Criativo ${c.title}`} />
                    <span className="crvCard__body">
                      <span className="crvCard__title">{c.title}</span>
                      <span className="cmpSub">
                        {c.versions} {c.versions === 1 ? "versão" : "versões"} · {fmtDateTime(c.updatedAt)}
                      </span>
                      {c.campaign ? <span className="cmpSub">{c.campaign.name}</span> : null}
                      <ReviewPill review={c.review} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
};

export default CriativosPage;
